import { useEffect, useState } from "hono/jsx";
import { Input } from "@/common/components/Input.tsx";
import { useValidatedInput } from "@/common/hooks/useValidatedInput.ts";
import { CreateApiTokenInput } from "@/features/auth/models.ts";

interface ApiToken {
  id: string;
  name: string;
  expiresAt: string | null;
  lastUsedAt: number | null;
  createdAt: number;
}

export const ApiTokenManager = () => {
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [generalError, setGeneralError] = useState("");
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Form Hooks
  const name = useValidatedInput({
    schema: CreateApiTokenInput.shape.name,
  });
  const expiresAt = useValidatedInput({
    schema: CreateApiTokenInput.shape.expiresAt,
  });

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const res = await fetch("/api/tokens");
      if (res.ok) {
        const data = await res.json();
        setTokens(data.tokens);
      }
    } catch (e) {
      console.error("Failed to fetch tokens", e);
    }
  };

  const createToken = async (e?: Event) => {
    if (e) e.preventDefault();

    // Validate all fields
    const isNameValid = name.validate(true);
    if (!isNameValid) return;

    setLoading(true);
    setGeneralError("");
    setNewToken(null);

    try {
      const res = await fetch("/api/tokens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.value.trim(),
          expiresAt: expiresAt.value || null,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setNewToken(data.token);
        name.setValue("");
        expiresAt.setValue("");
        fetchTokens();
      } else {
        if (data.details && Array.isArray(data.details)) {
          // deno-lint-ignore no-explicit-any
          data.details.forEach((d: any) => {
            if (d.field === "name") name.setError(d.message);
            if (d.field === "expiresAt") expiresAt.setError(d.message);
          });
        } else {
          setGeneralError(data.error || "Failed to create token");
        }
      }
    } catch (_e) {
      setGeneralError("Failed to create token");
    } finally {
      setLoading(false);
    }
  };

  const deleteToken = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/tokens/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchTokens();
      }
    } catch (e) {
      console.error("Failed to delete token", e);
    } finally {
      setLoading(false);
    }
  };

  const copyToken = async () => {
    if (newToken) {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div class="space-y-6">
      {/* Link to API Docs */}
      <div class="alert alert-info">
        <span class="font-medium">
          Use API tokens to authenticate requests programmatically.
        </span>
        <a href="/api/docs" class="btn btn-sm btn-ghost font-bold">
          View API Docs
        </a>
      </div>

      {/* New Token Display */}
      {newToken && (
        <div class="card bg-success text-success-content shadow-xl">
          <div class="card-body">
            <h3 class="card-title text-lg font-black uppercase">
              Token Created
            </h3>
            <p class="text-sm opacity-80">
              Copy this token now. You will not see it again.
            </p>
            <code class="bg-base-300 text-base-content px-4 py-3 rounded-lg font-mono text-sm break-all mt-2">
              {newToken}
            </code>
            <div class="card-actions justify-end mt-4">
              <button
                type="button"
                class="btn btn-ghost"
                onClick={() => setNewToken(null)}
              >
                Dismiss
              </button>
              <button
                type="button"
                class="btn btn-neutral"
                onClick={copyToken}
              >
                {copied ? "Copied!" : "Copy Token"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Token Form */}
      <div class="card bg-base-100 shadow-xl border border-base-300">
        <div class="card-body">
          <h3 class="card-title text-xl font-black uppercase tracking-tight">
            Create New Token
          </h3>

          <form onSubmit={createToken} class="flex flex-col gap-4 mt-4">
            <Input
              {...name.bind}
              label="Token Name"
              placeholder="e.g. L337 H4X0R B0T"
            />

            <Input
              {...expiresAt.bind}
              type="date"
              label="Expiration Date (Optional)"
              min={new Date().toISOString().split("T")[0]}
            />

            {generalError && (
              <div class="text-error font-bold text-sm">{generalError}</div>
            )}

            <div class="card-actions justify-end">
              <button
                type="submit"
                class={`btn btn-primary font-black uppercase ${
                  loading ? "loading" : ""
                }`}
                disabled={loading}
              >
                Generate Token
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Token List */}
      {tokens.length > 0 && (
        <div class="space-y-4">
          <h3 class="text-xs uppercase font-black opacity-30 tracking-widest text-center">
            Your API Tokens
          </h3>
          <div class="grid gap-2">
            {tokens.map((t) => (
              <div
                key={t.id}
                class="flex items-center justify-between p-4 bg-base-100 rounded-xl shadow-md border border-base-300 border-l-4 border-l-primary"
              >
                <div class="flex flex-col items-start gap-1">
                  <span class="font-black text-lg">{t.name}</span>
                  <div class="flex gap-2 text-xs opacity-60">
                    <span>Created: {formatDate(t.createdAt)}</span>
                    {t.expiresAt && (
                      <span class="text-warning">
                        Expires: {new Date(t.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                    {t.lastUsedAt && (
                      <span>Last used: {formatDate(t.lastUsedAt)}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-ghost text-error font-black uppercase text-xs"
                  onClick={() => deleteToken(t.id)}
                  disabled={loading}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tokens.length === 0 && (
        <div class="text-center opacity-50 py-8">
          <p class="font-medium">No API tokens yet.</p>
          <p class="text-sm">Create one to get started with the API.</p>
        </div>
      )}
    </div>
  );
};
